import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, Copy, Mail } from "lucide-react";
import { TREATMENTS, STUDIO } from "@/lib/studio";
import { BOOKING_GROUPS } from "@/lib/prices";
import { buildMailto, buildRequestText, copyText } from "@/lib/request";
import { Field, inputCls } from "./Field";
import { RequestFallback } from "./RequestFallback";

const EMPTY = { name: "", email: "", phone: "", treatment: "", date: "", daytime: "", message: "", privacy: false };
const today = () => new Date().toISOString().slice(0, 10);

const validate = (f) => {
  const e = {};
  if (f.name.trim().length < 2) e.name = "Bitte gib deinen Vor- und Nachnamen ein.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) e.email = "Bitte gib eine gültige E-Mail-Adresse ein.";
  if (!f.privacy) e.privacy = "Bitte bestätige die Datenschutzhinweise.";
  return e;
};

export const BookingForm = ({ preselect, onOpenPrivacy }) => {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [sentText, setSentText] = useState("");

  useEffect(() => {
    if (preselect) setForm((f) => ({ ...f, treatment: preselect }));
  }, [preselect]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const check = () => {
    const e = validate(form);
    setErrors(e);
    if (Object.keys(e).length) {
      toast.error("Bitte prüfe die markierten Felder.");
      return null;
    }
    return buildRequestText(form);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const text = check();
    if (!text) return;
    setSentText(text);
    window.location.href = buildMailto(text);
  };

  const onCopy = async () => {
    const text = check();
    if (!text) return;
    setSentText(text);
    const ok = await copyText(text);
    if (ok) toast.success("Anfrage kopiert – füge sie in eine E-Mail an " + STUDIO.email + " ein.");
    else toast.error("Kopieren nicht möglich. Bitte markiere den Text manuell.");
  };

  return (
    <form onSubmit={onSubmit} noValidate data-testid="booking-form" className="space-y-7">
      <div className="grid gap-7 sm:grid-cols-2">
        <Field id="name" label="Vor- und Nachname" required error={errors.name}>
          <input id="name" data-testid="booking-name-input" autoComplete="name" value={form.name} onChange={set("name")} className={inputCls} placeholder="z. B. Anna Schneider" />
        </Field>
        <Field id="email" label="E-Mail-Adresse" required error={errors.email}>
          <input id="email" type="email" inputMode="email" data-testid="booking-email-input" autoComplete="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="name@beispiel.de" />
        </Field>
        <Field id="phone" label="Telefon" hint="optional">
          <input id="phone" type="tel" inputMode="tel" data-testid="booking-phone-input" autoComplete="tel" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="Für eine schnelle Rückmeldung" />
        </Field>
        <Field id="treatment" label="Gewünschte Behandlung">
          <select id="treatment" data-testid="booking-treatment-select" value={form.treatment} onChange={set("treatment")} className={`${inputCls} appearance-none bg-[length:12px] bg-[right_0.25rem_center] bg-no-repeat select-chevron`}>
            <option value="">Bitte wählen</option>
            <optgroup label="Fußpflege & Fußmassage">
              {TREATMENTS.map((t) => (
                <option key={t.id} value={t.title}>{t.title}</option>
              ))}
            </optgroup>
            {BOOKING_GROUPS.map((g) => (
              <optgroup key={g.label} label={g.label}>
                {g.options.map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </optgroup>
            ))}
            <option value="Beratung / noch unsicher">Beratung / noch unsicher</option>
          </select>
        </Field>
        <Field id="date" label="Wunschtermin" hint="optional">
          <input id="date" type="date" min={today()} data-testid="booking-date-input" value={form.date} onChange={set("date")} className={inputCls} />
        </Field>
        <Field id="daytime" label="Bevorzugte Tageszeit" hint="optional">
          <select id="daytime" data-testid="booking-daytime-select" value={form.daytime} onChange={set("daytime")} className={`${inputCls} appearance-none select-chevron`}>
            <option value="">Flexibel</option>
            <option value="vormittags">Vormittags</option>
            <option value="mittags">Mittags</option>
            <option value="nachmittags">Nachmittags</option>
          </select>
        </Field>
      </div>
      <Field id="message" label="Nachricht" hint="optional">
        <textarea id="message" rows={4} data-testid="booking-message-input" value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} placeholder="Was möchtest du mir mitteilen?" />
        <p className="mt-2 text-xs text-ink-soft">Bitte keine ausführlichen Gesundheitsangaben – Details besprechen wir gern persönlich.</p>
      </Field>

      <div>
        <label htmlFor="privacy" className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-ink-soft">
          <input id="privacy" type="checkbox" data-testid="booking-privacy-checkbox" checked={form.privacy} onChange={set("privacy")} className="mt-1 h-[18px] w-[18px] shrink-0 accent-[#A8811A]" aria-invalid={!!errors.privacy} />
          <span>
            Ich habe die{" "}
            <button type="button" data-testid="booking-privacy-link" onClick={onOpenPrivacy} className="text-gold-deep underline underline-offset-4">Datenschutzhinweise</button>{" "}
            gelesen und bin einverstanden, dass meine Angaben zur Bearbeitung meiner Terminanfrage per E-Mail verwendet werden. *
          </span>
        </label>
        {errors.privacy && <p role="alert" data-testid="booking-privacy-error" className="mt-2 pl-8 text-xs text-[#A23B3B]">{errors.privacy}</p>}
      </div>

      <div className="space-y-4">
        <button type="submit" data-testid="booking-submit-btn" className="btn-gold group inline-flex w-full items-center justify-center gap-3 rounded-full px-6 py-4 text-[12px] font-semibold uppercase tracking-[0.2em] sm:text-[13px]">
          <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
          Terminanfrage per E-Mail senden
        </button>
        <div className="flex flex-col items-center justify-center gap-x-6 gap-y-2 text-sm text-ink-soft sm:flex-row">
          <span>Kein E-Mail-Programm?</span>
          <button type="button" data-testid="booking-copy-btn" onClick={onCopy} className="inline-flex items-center gap-2 text-gold-deep">
            <Copy className="h-4 w-4" /> <span className="link-underline">Anfrage kopieren</span>
          </button>
          <a href={`mailto:${STUDIO.email}`} data-testid="booking-address-link" className="inline-flex items-center gap-2 text-gold-deep">
            <Mail className="h-4 w-4" /> <span className="link-underline">{STUDIO.email}</span>
          </a>
        </div>
      </div>

      <p data-testid="booking-notice" className="border-t border-gold/30 pt-6 text-center font-serif text-lg italic leading-snug text-ink">
        „Deine Anfrage ist noch keine verbindliche Terminbestätigung. Ich melde mich persönlich bei dir, um deinen Termin abzustimmen.“
      </p>

      {sentText && <RequestFallback text={sentText} onCopy={onCopy} />}
    </form>
  );
};
